import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { MEMORIAL } from "@/constants/testIds";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { MemorialPreview } from "./CreateMemorial";
import MemorialEngagement from "@/components/MemorialEngagement";
import SocialShare from "@/components/SocialShare";
import MemorialStoryCard from "@/components/MemorialStoryCard";
import { ECARD_LANGUAGES } from "@/lib/ecardI18n";
import DonationWidget from "@/components/DonationWidget";
import PdfFramedPhotograph from "@/components/PdfFramedPhotograph";

export default function ViewMemorial() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const cardRef = useRef(null);
  const storyRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingStory, setDownloadingStory] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [poemLanguage, setPoemLanguage] = useState("English");
  const [translatedPoem, setTranslatedPoem] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [confirmingStripe, setConfirmingStripe] = useState(false);

  const fetchMemorial = () =>
    apiClient.get(`/memorials/${slug}`)
      .then(({ data }) => setM(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Memorial not found"));

  useEffect(() => {
    setLoading(true);
    fetchMemorial().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [slug]);

  // Handle Stripe post-checkout redirect: ?upgrade_session=CS_ID
  useEffect(() => {
    const sess = searchParams.get("upgrade_session");
    const upgraded = searchParams.get("upgraded");
    const canceled = searchParams.get("upgrade_canceled");
    if (canceled) {
      toast("Upgrade canceled. Your demo memorial is still here.");
      searchParams.delete("upgrade_canceled");
      setSearchParams(searchParams, { replace: true });
    }
    if (upgraded) {
      toast.success("Memorial upgraded — welcome to premium ✦");
      searchParams.delete("upgraded");
      setSearchParams(searchParams, { replace: true });
      fetchMemorial();
    }
    if (sess) {
      setConfirmingStripe(true);
      apiClient.get(`/payments/checkout/status/${sess}`)
        .then(({ data }) => {
          if (data?.payment_status === "paid") {
            toast.success("Payment received ✦ this memorial now lives here forever.");
            fetchMemorial();
          } else {
            toast("Still confirming your payment — refresh in a moment.");
          }
        })
        .catch(() => toast.error("Could not confirm the payment."))
        .finally(() => {
          setConfirmingStripe(false);
          searchParams.delete("upgrade_session");
          setSearchParams(searchParams, { replace: true });
        });
    }
    // eslint-disable-next-line
  }, [searchParams.get("upgrade_session"), searchParams.get("upgraded"), searchParams.get("upgrade_canceled")]);

  const onLanguageChange = async (e) => {
    const lang = e.target.value;
    setPoemLanguage(lang);
    if (lang === "English") {
      setTranslatedPoem(null);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await apiClient.post(
        `/memorials/${slug}/poem-translate`,
        { language: lang },
        { timeout: 90000 }
      );
      setTranslatedPoem(data.poem);
      toast.success(`Translated to ${lang} ✦`);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not translate. Please try again.");
      setPoemLanguage("English");
      setTranslatedPoem(null);
    } finally {
      setTranslating(false);
    }
  };

  // Build the share-friendly URL that renders rich social previews (OG tags).
  // Bots (Facebook, X, iMessage, WhatsApp, LinkedIn) scrape /api/s/{slug};
  // humans clicking the link are instantly redirected to /memorial/{slug}.
  const shareUrl = useMemo(() => {
    if (!m?.share_slug) return window.location.href;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
    return `${base}/api/s/${m.share_slug}`;
  }, [m?.share_slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share it lovingly.");
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `In memory of ${m.pet_name}`, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const shareTo = (platform) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(`In loving memory of ${m.pet_name} ✦`);
    let target = "";
    if (platform === "facebook")  target = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (platform === "x")         target = `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    if (platform === "whatsapp")  target = `https://api.whatsapp.com/send?text=${t}%20${u}`;
    if (target) window.open(target, "_blank", "noopener,noreferrer,width=640,height=560");
  };

  const downloadAsImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#FDFBF7",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${m.pet_name.replace(/[^a-z0-9]/gi, "_")}_memorial.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        apiClient.post(`/memorials/${m.share_slug}/track`, { event: "download_click", channel: "memorial" }).catch(() => {});
        toast.success("Memorial saved to your device ✦");
      }, "image/png");
    } catch (e) {
      console.error(e);
      toast.error("Could not save image. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadStory = async () => {
    if (!storyRef.current) return;
    setDownloadingStory(true);
    try {
      const canvas = await html2canvas(storyRef.current, {
        backgroundColor: "#FDFBF7",
        scale: 1, // already 1080x1920 native
        useCORS: true,
        logging: false,
        width: 1080,
        height: 1920,
      });
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${m.pet_name.replace(/[^a-z0-9]/gi, "_")}_story.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        apiClient.post(`/memorials/${m.share_slug}/track`, { event: "download_click", channel: "story" }).catch(() => {});
        toast.success("Story-sized image saved (1080×1920) ✦ ready for IG/TikTok Stories");
      }, "image/png");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate Story image.");
    } finally {
      setDownloadingStory(false);
    }
  };

  const embedCode = m ? `<iframe src="${window.location.origin}/embed/memorial/${m.share_slug}" width="460" height="640" frameborder="0" style="border:0;max-width:100%;" title="In memory of ${m.pet_name}"></iframe>` : "";

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      apiClient.post(`/memorials/${m.share_slug}/track`, { event: "share_click", channel: "embed" }).catch(() => {});
      toast.success("Embed code copied ✦ paste it into any website or blog.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center font-serif-display italic text-[var(--rb-text-muted)]">Awakening the memorial…</div>;
  }
  if (err) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center font-serif-display italic text-[var(--rb-text-muted)]">{err}</div>;
  }

  const isPremium = !!m.is_premium;
  const isDemo = !isPremium;
  const upgradeHref = `/memorial/${m.share_slug}/upgrade`;

  const pet = {
    pet_name: m.pet_name,
    species: m.species,
    dates: m.dates,
    traits: "",
    memory: "",
    owner_display_name: m.owner_display_name,
  };
  const scenery = m.scenery_image_base64
    ? { image_base64: m.scenery_image_base64, mime_type: m.scenery_mime_type || "image/png" }
    : null;
  const petPhoto = m.pet_photo_base64
    ? { base64: m.pet_photo_base64, mime: m.pet_photo_mime_type || "image/jpeg" }
    : null;

  return (
    <main className="relative max-w-4xl mx-auto px-6 md:px-12 py-16">
      <div className="text-center mb-10">
        <div className="tracking-celestial text-[var(--rb-gold)]">
          {isPremium ? "A memorial shared with the world" : "A 3-day demo memorial"}
        </div>
      </div>

      {isDemo && (
        <UpgradeBanner memorial={m} confirming={confirmingStripe} />
      )}

      <div className={isDemo ? "demo-watermark-wrap" : ""} ref={cardRef}>
        <MemorialPreview pet={pet} scenery={scenery} poem={translatedPoem || m.poem} petPhoto={petPhoto} eulogy={m.eulogy} />
        {isDemo && (
          <>
            <div className="demo-stamp" data-testid="demo-watermark-stamp">Demo Preview</div>
            <div className="demo-stamp-diagonal" aria-hidden><span>Demo · The Heirloom Pet</span></div>
          </>
        )}
      </div>

      {m.poem && isPremium && (
        <div className="mt-8 max-w-2xl mx-auto text-center" data-testid="memorial-poem-translate">
          <div className="inline-flex flex-wrap items-center justify-center gap-3 glass px-5 py-3 rounded-full">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--rb-text-muted)]">Read in another language</span>
            <select
              value={poemLanguage}
              onChange={onLanguageChange}
              disabled={translating}
              className="bg-transparent border-b border-[var(--rb-gold)]/40 px-2 py-1 text-[13px] text-[var(--rb-text-heading)] font-serif-display italic focus:outline-none focus:border-[var(--rb-gold)] disabled:opacity-50"
              data-testid="memorial-poem-language-select"
            >
              {ECARD_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            {translating && (
              <span className="text-[11px] italic text-[var(--rb-gold)]">Translating…</span>
            )}
          </div>
          {translatedPoem && !translating && (
            <p className="mt-3 text-[11px] italic text-[var(--rb-text-muted)]">
              Translated by Claude Sonnet 4.5 · The original verse remains unchanged ✦
            </p>
          )}
        </div>
      )}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <button onClick={shareNative} className="btn-gold" data-testid={MEMORIAL.shareBtn}>
          Share
        </button>
        <button onClick={copyLink} className="btn-ghost" data-testid={MEMORIAL.shareLink}>
          Copy link
        </button>
        <button
          onClick={() => shareTo("facebook")}
          className="btn-ghost !px-4"
          aria-label="Share on Facebook"
          title="Share on Facebook"
          data-testid="memorial-share-facebook"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.1v2.3H7.7V14h2.7v8h3.1z"/></svg>
        </button>
        <button
          onClick={() => shareTo("x")}
          className="btn-ghost !px-4"
          aria-label="Share on X"
          title="Share on X"
          data-testid="memorial-share-x"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.573L23 22h-6.828l-5.35-6.99L4.5 22H1.244l8.03-9.176L1 2h6.994l4.834 6.393L18.244 2zm-1.2 18h1.856L7.05 4H5.06l11.985 16z"/></svg>
        </button>
        <button
          onClick={() => shareTo("whatsapp")}
          className="btn-ghost !px-4"
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
          data-testid="memorial-share-whatsapp"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5H8c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.2 5 4.4 2.4 1 3 .8 3.5.7.5-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4 0-.1-.2-.2-.5-.3zM12 22c-1.8 0-3.5-.5-5-1.4L2 22l1.4-5c-1-1.5-1.4-3.2-1.4-5 0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10z"/></svg>
        </button>
        <button onClick={downloadAsImage} disabled={downloading} className="btn-ghost" data-testid="memorial-download-btn">
          {downloading ? "Saving…" : "Download · poster"}
        </button>
        <button onClick={downloadStory} disabled={downloadingStory} className="btn-ghost" data-testid="memorial-download-story-btn">
          {downloadingStory ? "Saving…" : "Download · Story"}
        </button>
        <button onClick={() => setEmbedOpen((v) => !v)} className="btn-ghost" data-testid="memorial-embed-btn">
          {embedOpen ? "Close embed" : "Embed code"}
        </button>
        <a href="/create" className="btn-ghost">Create another</a>
      </div>

      {embedOpen && (
        <div className="mt-6 max-w-2xl mx-auto glass p-6 rounded-sm" data-testid="memorial-embed-panel">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Embed on your website</div>
          <p className="font-serif-display italic text-[var(--rb-text-body)] text-[15px] mb-4">
            Paste this snippet anywhere — a personal blog, a Wix or Squarespace site, a Notion page —
            and {m.pet_name}&apos;s memorial will appear right there.
          </p>
          <textarea readOnly value={embedCode} className="rb-textarea text-xs font-mono" rows={3} onClick={(e) => e.target.select()} data-testid="memorial-embed-textarea"/>
          <div className="mt-4 flex items-center justify-between">
            <button onClick={copyEmbed} className="btn-gold" data-testid="memorial-embed-copy-btn">Copy embed code</button>
            <a href={`/embed/memorial/${m.share_slug}`} target="_blank" rel="noreferrer" className="text-[11px] tracking-[0.22em] uppercase text-[var(--rb-gold)] hover:opacity-80">Preview ↗</a>
          </div>
        </div>
      )}

      {/* Social share row */}
      <div className="mt-6 flex justify-center">
        <SocialShare slug={m.share_slug} petName={m.pet_name} />
      </div>

      {/* Off-screen Story card rendered for html2canvas capture */}
      <div style={{ position: "absolute", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
        <div ref={storyRef}>
          <MemorialStoryCard memorial={m} />
        </div>
      </div>

      {/* Candle + Tributes */}
      <MemorialEngagement slug={m.share_slug} petName={m.pet_name} locked={isDemo} upgradeHref={upgradeHref} />

      {/* Commission CTA — gentle lead funnel */}
      <section className="mt-20">
        <div className="relative overflow-hidden rounded-sm border border-[var(--rb-gold)]/30 bg-gradient-to-br from-[#FDFBF7] via-white to-[#FAF6EC]">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }}/>
          <div className="relative p-10 md:p-14 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="tracking-celestial text-[var(--rb-gold)] mb-3">A keepsake worthy of {m.pet_name}</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[var(--rb-text-heading)] leading-tight">
                Bring this scene<br/><span className="italic">into your home</span> — as a handcrafted diorama.
              </h3>
              <p className="mt-5 font-serif-display italic text-[17px] text-[var(--rb-text-body)] leading-relaxed">
                Sculpted by hand, sealed under glimmering glass, and inscribed with the very poem written for {m.pet_name}.
                An heirloom that outlives any photograph — to keep on a shelf, in the light, forever.
              </p>
            </div>
            <div className="md:col-span-2 md:text-right">
              <div className="inline-flex items-center gap-3 px-5 py-2 mb-5 rounded-full border border-[var(--rb-gold)]/40 bg-white/70">
                <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">Starting from</span>
                <span className="font-serif-display text-2xl text-[var(--rb-gold)]">$150</span>
              </div>
              <div>
                <a href={`/commission?memorial=${m.share_slug}`} className="btn-gold" data-testid="memorial-commission-cta">
                  Commission this diorama
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                </a>
              </div>
              <p className="text-[11px] text-[var(--rb-text-muted)] italic mt-3">Reply within 1–2 days · No obligation</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-2xl mx-auto mt-16 px-4 space-y-8">
        {isPremium && <PdfFramedPhotograph memorial={m} />}
        <DonationWidget memorialSlug={m.share_slug} />
      </section>
    </main>
  );
}


function UpgradeBanner({ memorial, confirming }) {
  // Countdown to expiry (updates every 60s)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const remaining = useMemo(() => {
    if (!memorial.expires_at) return null;
    const ms = new Date(memorial.expires_at).getTime() - now;
    if (ms <= 0) return { expired: true, label: "expired" };
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days >= 1) return { label: `${days} day${days === 1 ? "" : "s"}` };
    if (hours >= 1) return { label: `${hours} hour${hours === 1 ? "" : "s"}` };
    const mins = Math.max(1, Math.floor(ms / (1000 * 60)));
    return { label: `${mins} minute${mins === 1 ? "" : "s"}` };
  }, [memorial.expires_at, now]);

  return (
    <div
      data-testid="memorial-upgrade-banner"
      className="mb-10 relative overflow-hidden rounded-sm border border-[var(--rb-gold)]/50 bg-gradient-to-br from-white via-[#FDFBF7] to-[#FAF6EC] shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }} />
      <div className="relative p-8 md:p-10 grid md:grid-cols-5 gap-6 items-center">
        <div className="md:col-span-3">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Demo memorial</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--rb-text-heading)] leading-tight">
            Keep <span className="italic">{memorial.pet_name}&apos;s</span> memorial<br/>alive forever — $29 one-time.
          </h2>
          <ul className="mt-5 space-y-1.5 font-serif-display italic text-[15px] text-[var(--rb-text-body)]">
            <li>✦ Permanent hosting — never expires</li>
            <li>✦ Loved ones can light candles &amp; leave tributes</li>
            <li>✦ Shareable link + eCards</li>
            <li>✦ Translate the poem into any language</li>
            <li>✦ Watermark is removed</li>
          </ul>
          {remaining && !remaining.expired && (
            <p className="mt-4 text-[12px] tracking-[0.22em] uppercase text-[var(--rb-text-muted)]" data-testid="memorial-demo-countdown">
              This demo disappears in <span className="text-[var(--rb-gold)]">{remaining.label}</span>
            </p>
          )}
          {remaining && remaining.expired && (
            <p className="mt-4 text-[12px] tracking-[0.22em] uppercase text-red-500">
              This demo has expired — upgrade now to restore it.
            </p>
          )}
        </div>
        <div className="md:col-span-2 md:text-right">
          <div className="inline-flex items-center gap-3 px-5 py-2 mb-5 rounded-full border border-[var(--rb-gold)]/40 bg-white/70">
            <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">One-time</span>
            <span className="font-serif-display text-3xl text-[var(--rb-gold)]">$29</span>
          </div>
          <div>
            <Link to={`/memorial/${memorial.share_slug}/upgrade`} className="btn-gold" data-testid="memorial-upgrade-cta">
              {confirming ? "Confirming…" : "Keep this memorial forever"}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            </Link>
          </div>
          <p className="text-[11px] text-[var(--rb-text-muted)] italic mt-3">Pay once · lives here forever · Stripe or PayPal</p>
        </div>
      </div>
    </div>
  );
}
